import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
// eslint-disable-next-line import/no-unresolved
import SSOLogin from "@regional/components/SSOLogin";
import LocalLoader from "../../components/Loaders/Local";

import { useGetTokenQuery, useGetMeQuery } from "../../api/auth.api.slice";
import { getLogin, logout } from "../../features/auth/slices/auth.slice";
import { resetSessionData } from "../../features/auth/slices/userSession.slice";

import { authorize } from "../../services/auth.service";

const getErrorMessage = (error) => {
  const { message, error: errorMessage } = error?.data || {};
  let msg = "Could not process request, please try later.";
  if (typeof message === "string") msg = message;
  if (typeof errorMessage === "string") msg = errorMessage;
  return msg;
};

function CallbackPage() {
  const { status } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tenantId, postLoginRoute } = useSelector(
    (state) => state.userSession.info
  );
  const [message, setMessage] = useState({ type: "", msg: "" });

  const {
    data: token,
    isLoading: tokenIsLoading,
    error: tokenError,
    isSuccess: tokenIsSuccess,
  } = useGetTokenQuery(null, { skip: status !== "success" });

  const {
    data: me,
    isLoading: meIsLoading,
    error: meError,
    isSuccess: meIsSuccess,
  } = useGetMeQuery(null, { skip: status !== "success" });

  useEffect(() => {
    if (tokenIsSuccess && meIsSuccess) {
      setMessage({ type: "", msg: "" });
      const { permissions = [] } = me;
      authorize(token, permissions);
      dispatch(getLogin());
      dispatch(resetSessionData());
      if (token) {
        if (token.addNewUser) navigate("/register", { replace: true });
        else {
          const { _id } = token?.currentTenant || {};
          let navigateTo = postLoginRoute ?? "/licenses";
          if (navigateTo.includes("applications") && tenantId !== _id)
            navigateTo = "/applications";
          navigate(navigateTo, { replace: true });
          localStorage.removeItem("postLoginRoute");
        }
      } else {
        dispatch(logout());
      }
    }
  }, [
    token,
    tokenIsSuccess,
    me,
    meIsSuccess,
    dispatch,
    postLoginRoute,
    navigate,
    status,
    tenantId,
  ]);

  useEffect(() => {
    const error = tokenError ?? meError;
    let type = "error";
    let msg = getErrorMessage(error);
    if (status && status !== "success") {
      const successMessage = searchParams.get("message");
      if (successMessage) {
        type = "info";
        msg = successMessage;
      } else {
        msg =
          searchParams.get("error") ||
          "Failed to login, please try again later";
      }
    }
    setMessage({ type, msg });
  }, [tokenError, meError, navigate, status, searchParams]);

  const isLoading = tokenIsLoading || meIsLoading;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 8rem)",
          flexDirection: "column",
          gap: 2,
          mx: "auto",
        }}
      >
        {isLoading && (
          <Typography sx={{ textAlign: "center" }}>
            Signing you in, please wait...
          </Typography>
        )}
        {!isLoading && <SSOLogin message={message} />}
      </Box>
      <LocalLoader progress={isLoading || false} />
    </>
  );
}

export default CallbackPage;
